'use client';
import { AppDispatch, RootState } from '@/redux/store';
import { EXIST_LOCAL_STORAGE } from '@/services/constants/app';
import { getCookie, isObjectsEqual, setEncodetext, setStorage } from '@/services/helperFunctions';
import { setCookie } from '@/services/helperFunctions/serverHelperFunctions';
import { Bond, Result } from '@/services/interface/bonds';
import { ISearchFilters } from '@/services/interface/searchForm';
import { makeNavigationSiteAnalytics } from '@/services/simpleApis';
import {
    faAngleLeft,
    faAngleRight,
    faCircleChevronLeft,
    faCircleChevronRight,
    faEllipsisVertical,
    faGear,
    faList,
    faMinus,
    faPlus,
    faSearch,
    faSortDown,
    faSortUp,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { FC, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { Bookmark, BookmarkCheckFill } from 'react-bootstrap-icons';
import Skeleton from 'react-loading-skeleton';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as bondsAction from '../../redux/actions/bonds';
import * as commonAction from '../../redux/actions/common';
import * as dashboardAction from '../../redux/actions/dashboard';
import * as authenticateAction from '../../redux/actions/authenticate';
import './searchFormStyles.css';

// Lazy imports
const Pagination = dynamic(() => import('../common/Pagination'), {
    ssr: false,
});
const BondPosition = dynamic(() => import('./BondPosition'), {
    ssr: false,
});

const SearchFilters = dynamic(() => import('./SearchFilters'), {
    ssr: true,
    loading: () => (
        <Skeleton
            height={40}
            className="col-11 d-block mx-auto border my-2 py-3 px-3"
            baseColor="#f0f5ffff"
            // highlightColor="#c0ccdfff"
            borderRadius={5}
            style={{ borderBottom: '2px solid #99adcaff', backgroundColor: '#f0f5ffff' }}
            inline
        />
    ),
});
const SearchForm = dynamic(() => import('./SearchForm'), {
    ssr: true,
    loading: () => <SearchFormLazyLoading />,
});

const SearchFormAccordion = dynamic(() => import('./SearchFormAccordion'), {
    ssr: true,
    loading: () => <Skeleton height={30} className="col-11 d-block mx-auto border" inline />,
});

import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BONDS_ACTIONS } from '@/redux/actionType/bonds';
import { issuerBondsCountData } from '@/types/issuerIndexTypes';
import { useRouter } from 'next/navigation';
import TableLazyLoading from './TableLazyLoading';
import SearchFormLazyLoading from './searchFormComponents/SearchFormLazyLoading';

interface IBondsearch {
    initialBondsData: Result;
    isIssuerFirstletterList: issuerBondsCountData[];
}

type BondSearchProps = IBondsearch & PropsFromRedux;

const BondSearch: FC<BondSearchProps> = ({
    getBonds,
    bondsData,
    noFilterBonds,
    isBoandsLoader,
    initialBondsData,
    countryData,
    maturityData,
    isIssuerListByCharLoader,
    currencyData,
    getAdsKeyExists,
    createAdsKeyExists,
    getIssuersListByChar,
    isIssuerListByCharData,
    getNoFilterBonds,
    isIssuerFirstletterList,
    isIssuerFirstletterListLoader,
    getNotification,
    trigger,
    bondsFulltextHandle,
    getfollowBond,
    ...rest
}) => {
    const dispatch = useDispatch();
    // Always use initialBondsData for first render to ensure SSR data appears in source
    const [currentData, setCurrentData] = useState<Result>(initialBondsData);
    const [filterDataList, setFilterDataList] = useState<Record<string, any>>({});
    const [filterCloseList, setFilterCloselist] = useState<Record<string, any>>({});
    const [isInitialRender, setIsInitialRender] = useState(true);
    const skipNextFetch = useRef(false); // Ref to skip Bonds fetching for reset
    const hasSkippedFirstFetch = useRef(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(25);
    const [yieldColumn, setYieldColumn] = useState('market_yield');
    const [changePercentageColumn, setchangePercentageColumn] = useState('change_percentage');
    // Sorting states
    const [sortColumn, setSortColumn] = useState('bond_name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showChange_percent, setShowchange_percent] = useState(false);

    const filtersCookieData = useMemo(() => getCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ), []);

    const filtersCookieDataParsed: ISearchFilters | null = useMemo(
        () => (filtersCookieData ? JSON.parse(filtersCookieData) : null),
        [filtersCookieData]
    );

    // const initMaturitySliderRange: maturityDataForSliderType = {
    //     min_maturity_year: new Date().getFullYear(),
    //     max_maturity_year: new Date().getFullYear() + 100,
    //     status_min_maturity_year: new Date().getFullYear(),
    //     status_max_maturity_year: new Date().getFullYear() + 100,
    // };

    // const maturityDataForSlider: maturityDataForSliderType = useMemo(
    //     () =>
    //         maturityData?.data && Array.isArray(maturityData.data) && maturityData.data[0] ? maturityData.data[0] : initMaturitySliderRange,
    //     [maturityData?.data?.length]
    // );

    const currentYear = new Date().getFullYear();
    const min_maturity_year = currentYear;
    const max_maturity_year = currentYear + 100;

    const initFilters = useMemo(
        () =>
            ({
                user_id: filtersCookieDataParsed?.user_id || '',
                country: '',
                currency: '',
                fulltext: '',
                region: '',
                industry: '',
                coupon_from: 0,
                coupon_to: 30,
                yield_from: 0,
                yield_to: 30,
                maturity_from_year: min_maturity_year,
                maturity_to_year: max_maturity_year,
                callable: '',
                issue_type: 'All bonds',
                status: false,
                page_no: 1,
                per_page: 25,
                sorting_column_sorting_order: 'bond_name asc',
                issuer: '',
                issuer_search_type: '',
            } as ISearchFilters),
        [filtersCookieDataParsed]
    );

    const initFiltersFromCookies = useMemo(
        () => (filtersCookieDataParsed ? filtersCookieDataParsed : initFilters),
        [filtersCookieDataParsed, initFilters]
    );

    if (!!initialBondsData.error) {
        console.log('initialBondsData.error', initialBondsData.error);
    } else {
        console.log('Rendering BondSearch component with initialBondsData bonds length:', initialBondsData.bonds?.length);
    }

    const [filterObj, setFilterObj] = useState<ISearchFilters>(initFiltersFromCookies);

    const router = useRouter();

    const handleReset = useCallback(async () => {
        const resetFilters = { ...initFilters, maturity_from_year: min_maturity_year, maturity_to_year: max_maturity_year };
        console.log('resetFilters', resetFilters);
        if (!isObjectsEqual(resetFilters, filterObj)) skipNextFetch.current = true;
        setFilterObj(resetFilters);
        setCurrentPage(1);
        setRecordsPerPage(25);
        setFilterDataList(resetFilters);
        setCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify(initFilters));
        router.push('/screener');
    }, [noFilterBonds, initFilters, min_maturity_year, max_maturity_year, filterObj]);

    const handleSort = useCallback(
        (column: string) => {
            const isSameColumn = sortColumn === column;

            const newDirection = isSameColumn && sortDirection === 'asc' ? 'desc' : 'asc';

            setSortColumn(column);
            setSortDirection(newDirection);

            setFilterObj((prev: ISearchFilters) => ({
                ...prev,
                sorting_column_sorting_order: `${column} ${newDirection}`,
            }));
        },
        [sortColumn, sortDirection]
    );

    // Generate sort indicators for column headers
    const getSortIndicator = useCallback(
        (column: string) => {
            if (sortColumn !== column) return null;
            return sortDirection === 'asc' ? (
                <FontAwesomeIcon size="sm" icon={faSortUp} />
            ) : (
                <FontAwesomeIcon size="sm" icon={faSortDown} />
            );
        },
        [sortColumn, sortDirection]
    );

    useEffect(() => {
        if (initialBondsData && isInitialRender) {
            setIsInitialRender(false);
        }

        // Dispatch server side fetched data to redux
        if (initialBondsData.bonds) {
            console.log('Dispatching initialBondsData to redux bondsData', initialBondsData);
            dispatch({
                type: BONDS_ACTIONS.GET_BONDS,
                payload: initialBondsData,
            });
            setCurrentData(initialBondsData);
        }

        // if (!noFilterBonds) {
        //     getNoFilterBonds({ params: { ...initFilters, status: initFilters.status ? '' : 'Active' } });
        // }

        return () => {
            hasSkippedFirstFetch.current = false;
        };
    }, [initialBondsData]);

    // When Redux data updates, use it for subsequent renders
    useEffect(() => {
        if (bondsData && Object.keys(bondsData).length > 0 && !isInitialRender) {
            setCurrentData(bondsData);
        }
    }, [bondsData, isInitialRender]);

    const UserId = getCookie(EXIST_LOCAL_STORAGE.USER_ID) || '';

    const [testSlider, setTestSilder] = useState({
        test_from: 0,
        test_to: 30,
    });

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleGetList = useCallback(
        async (reqObj: Record<string, any>, isFromPage = false, should_update_applied_filter_data = true, delay = 0) => {
            try {
                const filters = {
                    ...filterObj,
                    page_no: isFromPage ? reqObj.page_no : 1,
                    per_page: recordsPerPage,
                    // Use reqObj values first, falling back to current state values
                    yield_from: reqObj?.yield_from ?? filterObj?.yield_from ?? 0,
                    yield_to: reqObj?.yield_to ?? filterObj?.yield_from ?? 30,
                    coupon_from: reqObj?.coupon_from !== undefined ? reqObj.coupon_from : filterObj.coupon_from,
                    coupon_to: reqObj?.coupon_to !== undefined ? reqObj.coupon_to : filterObj.coupon_to,
                    maturity_from_year:
                        reqObj?.maturity_from_year !== undefined
                            ? !reqObj.status
                                ? reqObj?.maturity_from_year > 2023
                                    ? reqObj?.maturity_from_year
                                    : 2023
                                : filterObj?.maturity_from_year
                            : filterObj.maturity_from_year,
                    maturity_to_year:
                        reqObj?.maturity_to_year !== undefined
                            ? !reqObj?.status && reqObj?.maturity_to_year < 2023
                                ? 2024
                                : reqObj?.maturity_to_year
                            : filterObj.maturity_to_year,
                    // Other reqObj values
                    ...reqObj,
                    user_id: UserId,
                    callable: reqObj.callable ? 'N' : '',
                    sorting_column_sorting_order: reqObj?.sorting_column_sorting_order
                        ? reqObj?.sorting_column_sorting_order
                        : 'bond_name ASC',
                };

                // Do not trigger loading for search form reset action
                if (skipNextFetch.current && !hasSkippedFirstFetch.current) setLoading(true);

                // We do not need first time as nextjs already in Server side
                if (!hasSkippedFirstFetch.current && !filterObj.user_id) {
                    hasSkippedFirstFetch.current = true;
                    return;
                }

                setFilterObj(filters);

                const body = {
                    params: { ...filters, status: filters.status ? '' : 'Active', issuer_search_type: filters.issuer_search_type || 'C' },
                };

                if (should_update_applied_filter_data) setFilterDataList(filters);

                setCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify(filters));

                if (timerRef?.current) clearTimeout(timerRef.current);

                if (skipNextFetch.current) {
                    skipNextFetch.current = false; // reset it after skipping
                    return;
                } else {
                    const newTimer = setTimeout(() => {
                        getBonds(body).then(() => {
                            hasSkippedFirstFetch.current = true;
                        });
                        if (document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur();
                        }
                    }, delay);
                    timerRef.current = newTimer;
                }
            } catch (e) {
                console.error('Error in handleGetList:', e);
            } finally {
                setLoading(false);
            }
        },
        [filterObj, getBonds, recordsPerPage, currentPage]
    );

    // UseEffect for handling fulltext
    useEffect(() => {
        if (trigger) {
            const data = {
                ...initFiltersFromCookies,
                issue_type: '',
                fulltext: bondsFulltextHandle.params?.fulltext,
                page_no: 1,
            };
            setFilterDataList(data);
            setFilterObj(data);
            handleGetList(data);
        }
    }, [trigger]);

    // UseEffect for handling search filters change
    useEffect(() => {
        if (hasSkippedFirstFetch.current)
            handleGetList(
                {
                    ...filterObj,
                    page_no: 1,
                    per_page: 25,
                },
                false,
                true,
                1200
            );

        // Reset page number and per page on search filters change
        setCurrentPage(1);
        setRecordsPerPage(25);
    }, [filterObj.issuer]);

    useEffect(() => {
        handleGetList({
            ...filterObj,
            page_no: 1,
            per_page: 25,
        });

        // Reset page number and per page on search filters change
        setCurrentPage(1);
        setRecordsPerPage(25);
    }, [
        filterObj.issuer_search_type,
        filterObj.issue_type,
        filterObj.region,
        filterObj.industry,
        filterObj.currency,
        filterObj.country,
        testSlider?.test_from,
        testSlider?.test_to,
        filterObj.yield_from,
        filterObj.yield_to,
        filterObj.coupon_from,
        filterObj.coupon_to,
        filterObj.callable,
        filterObj.status,
        filterObj?.maturity_from_year,
        filterObj?.maturity_to_year,
        sortColumn,
        sortDirection,
    ]);

    const handlePageChange = useCallback(
        (pageNumber: number) => {
            const new_filters = {
                ...filterObj,
                page_no: pageNumber,
            };
            handleGetList(new_filters);
            setFilterObj(new_filters);
            setCurrentPage(pageNumber);
        },
        [recordsPerPage, handleGetList]
    );

    const handleRecordsPerPage = useCallback(
        (records_per_page: number) => {
            const new_filters = {
                ...filterObj,
                per_page: records_per_page,
            };
            setFilterObj(new_filters);
            handleGetList(new_filters);
            setRecordsPerPage(records_per_page);
        },
        [handleGetList, filterObj]
    );

    const handleYieldClick = useCallback((val: string) => setTimeout(() => handleSort(val), 100), [handleSort]);

    const handleChangePercent = useCallback(
        (e: any) => {
            setShowchange_percent(!showChange_percent);
            setchangePercentageColumn(e.target.value);
            handleSort(showChange_percent ? 'changepercentage' : 'chng');
        },
        [showChange_percent]
    );

    const displayData = useMemo(() => (currentData.total_records ? currentData : initialBondsData), [currentData, initialBondsData]);

    const [issuer_char, setIssuer_char] = useState('');
    const [issuerSearchQuery, setIssuerSearchQuery] = useState('');
    const [issuerSearch_pageNo, setIssuerSearch_pageNo] = useState(1);

    type issuerListByCharType = {
        issuer: any[];
        page_no?: number;
        total_records?: number;
        per_page?: number;
    };
    const [issuerListByChar, setIssuerListByChar] = useState<issuerListByCharType>({ issuer: [] });

    const handlePressBack = () => {
        setIssuer_char('');
        setIssuerSearchQuery('');
        setIssuerSearch_pageNo(1);
    };

    useEffect(() => {
        const body = {
            issuer_char,
            issuer: issuerSearchQuery,
            page_no: issuerSearch_pageNo,
            per_page: 20,
        };
        if (issuer_char) {
            getIssuersListByChar(body).then((res: any) => {
                if (res.data) {
                    setIssuerListByChar(res.data);
                }
            });
        }
    }, [issuer_char, issuerSearch_pageNo]);

    const handleIssuerFirstletterListData = useCallback(() => {
        let res: any[] = [];
        isIssuerFirstletterList?.forEach((issuer: any) => {
            const issuerIndex = res.findIndex((x) => x?.issuer === issuer.issuer);
            issuerIndex !== -1
                ? (res[issuerIndex] = {
                      issuer: res[issuerIndex].issuer,
                      issuer_name: [...res[issuerIndex].issuer_name, issuer.issuer_name],
                  })
                : res.push({
                      issuer: issuer.issuer,
                      issuer_name: [issuer.issuer_name],
                  });
        });
        return res;
    }, [isIssuerFirstletterList]);

    const handleIssuerListShowAndHide = (issuer: string) => setIssuer_char(issuer);

    const currentPageNo = useMemo(() => issuerListByChar.page_no || 0, [issuerListByChar.page_no]);

    const issuerFirstletterListData = useMemo(() => handleIssuerFirstletterListData(), [handleIssuerFirstletterListData]);

    const lastPageNo = useMemo(
        () => (issuerListByChar.total_records ? Math.ceil(issuerListByChar.total_records / (issuerListByChar.per_page || 1)) : 0),
        [issuerListByChar.total_records, issuerListByChar.per_page]
    );

    const handleIssuerInputChange = (e: any) => {
        const { value } = e.target;
        setIssuerSearchQuery(value);

        if (timerRef.current) clearTimeout(timerRef.current);

        const body = {
            issuer_char,
            issuer: value,
            page_no: 1,
            per_page: 20,
        };
        const newTimer = setTimeout(() => {
            getIssuersListByChar(body).then((res: any) => {
                if (res.data) {
                    setIssuerListByChar(res.data);
                }
            });
        }, 500);
        timerRef.current = newTimer;
    };

    // const windowSize = useWindowSize();
    // const { width: windowWidth } = windowSize;

    const appliedFiltersContainerElement = useRef<HTMLDivElement>(null);
    // const appliedFiltersContainerHeight = appliedFiltersContainerElement.current?.offsetHeight || 0;
    // const heightToBeReduced = appliedFiltersContainerHeight + (windowWidth < 769 ? 310 : 165);
    // console.log('appliedFiltersContainerHeight', `${appliedFiltersContainerHeight}px`, windowWidth, heightToBeReduced);

    const handleBondNameClick = useCallback((isin: string) => makeNavigationSiteAnalytics('Bond name', '/bonds', isin), []);

    const handleFollowBond = useCallback(
        async (isin: string) => {
            const body = {
                isin,
                user_id: UserId,
            };
            const bondsList = displayData?.bonds ?? [];
            const updatedBondsList = bondsList.map((bond) => (bond.isin === isin ? { ...bond, follow_bond: !bond.follow_bond } : bond));
            setCurrentData((prev) => ({ ...(prev ?? {}), bonds: updatedBondsList }));
            // const bonds_data = { ...bondsData, bonds: updatedBondsList };
            // setStorage(EXIST_LOCAL_STORAGE.BOND_DATA, JSON.stringify(bonds_data));
            getfollowBond(body).then(() => {
                const body = {
                    params: {
                        user_id: UserId,
                        page_no: 1,
                    },
                };
                getNotification(body);
            });
        },
        [currencyData, displayData?.bonds]
    );

    const isLoggedIn = !!UserId;

    const [showPosition, setShowPosition] = useState(false);
    const [bondId, setBondId] = useState();
    const [selectedAction, setSelectedAction] = useState('buyPrice');

    const handleShowPosition = useCallback((bond_id: any, action: any) => {
        setShowPosition(true);
        setBondId(bond_id);
        setSelectedAction(action);
    }, []);

    const handleClosePosition = useCallback(() => setShowPosition(false), []);

    const isMobile = useMediaQuery('(max-width: 991px)');

    console.log(
        '[SERVER] hasSkippedFirstFetch.current',
        hasSkippedFirstFetch.current,
        'isBoandsLoader',
        isBoandsLoader,
        'loading',
        loading
    );

    return (
        <div className="container-fluid px-lg-3 mt-1">
            <div className="row flex-column px-lg-3 flex-lg-row">
                <Suspense>
                    <div className="d-lg-none mt-3">
                        {isMobile && (
                            <SearchFormAccordion
                                recordsPerPage={recordsPerPage}
                                filterData={filterDataList}
                                filterObj={filterObj}
                                setFilterObj={setFilterObj}
                                handleGetList={handleGetList}
                                handleReset={handleReset}
                                is_loading={isBoandsLoader || loading}
                                testSlider={testSlider}
                                setTestSilder={setTestSilder}
                                setFilterDataList={setFilterDataList}
                                filterCloseList={filterCloseList}
                                noFilterBonds={noFilterBonds}
                                isLoggedIn={isLoggedIn}
                                {...rest}
                            />
                        )}
                    </div>
                </Suspense>
                <div
                    className="col-lg-2 d-lg-block d-none py-1 px-0 shibui-border border-top-0 border-end-0 border-bottom-0 material-shadow rounded"
                    style={{ height: '90vh', overflowY: 'auto' }}
                >
                    <div className="d-flex justify-content-end my-2">
                        <div className="tooltip-cus col-11 mx-auto">
                            <nav
                                className=" link-secondary border text-center rounded hover-shibui-nav-link"
                                style={{ padding: '0.4rem 0' }}
                            >
                                <p className="mb-0 d-flex align-items-center">
                                    <label htmlFor="" className="col-1 px-1 " style={{ margin: '0 .25rem' }}>
                                        <FontAwesomeIcon icon={faList} color="#294672" size="xs" className="" />
                                    </label>
                                    <span
                                        className="col border border-0 mb-0 border-start text-start ps-3"
                                        style={{ fontSize: 12, color: '#1e4c82' }}
                                    >
                                        Issuer List
                                    </span>
                                    <FontAwesomeIcon size="lg" icon={faCircleChevronRight} color="#294672" className="col-3" />
                                </p>
                            </nav>
                            <span className="tooltiptext-cus col-12">
                                <div className="">
                                    <div className=" gradient-tooltip-head ">
                                        {!!issuer_char && (
                                            <p className="mb-0 cursor-pointer text-xs mt-1 text-white" onClick={handlePressBack}>
                                                <FontAwesomeIcon size="lg" icon={faCircleChevronLeft} color="#fff" className="pe-1" />
                                                Back
                                            </p>
                                        )}
                                        <h6 className=" text-center  rounded m-0 text-white">Issuers list </h6>
                                        {!!issuer_char && (
                                            <Link className="show-more-cus-tooltip no-underline text-xs" href={'/issuers/' + issuer_char}>
                                                Show more
                                            </Link>
                                        )}
                                    </div>
                                    <div className="mx-height-cus p-1 small-scroll-bar">
                                        {!isIssuerFirstletterListLoader ? (
                                            !issuer_char ? (
                                                issuerFirstletterListData.map((item, index) => (
                                                    <div className="col-1 p-1" style={{ height: 'max-content' }} key={index}>
                                                        <nav
                                                            className="cursor-pointer link-secondary material-shadow text-center p-2 py-1 rounded hover-shibui-nav-link"
                                                            style={{ fontSize: 11 }}
                                                            onClick={() => handleIssuerListShowAndHide(item.issuer)}
                                                        >
                                                            {item.issuer}
                                                        </nav>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="d-flex flex-column w-100">
                                                    <div className="search-bar-container d-flex justify-content-between align-items-center">
                                                        <span
                                                            className="d-flex align-items-center justify-content-center gap-4 font-size-12"
                                                            style={{ width: 'max-content' }}
                                                        >
                                                            <span
                                                                className={`link-secondary rounded p-1 d-flex align-items-center ${
                                                                    currentPageNo && currentPageNo == 1
                                                                        ? 'opacity-50 user-select-none'
                                                                        : 'cursor-pointer fw-semibold'
                                                                }`}
                                                                onClick={() =>
                                                                    !loading &&
                                                                    issuerSearch_pageNo > 1 &&
                                                                    setIssuerSearch_pageNo(issuerSearch_pageNo - 1)
                                                                }
                                                            >
                                                                <FontAwesomeIcon size="sm" icon={faAngleLeft} color="#999" /> Prev
                                                            </span>
                                                            {currentPageNo} of {lastPageNo}
                                                            <span
                                                                className={`link-secondary rounded p-1  d-flex align-items-center ${
                                                                    currentPageNo && currentPageNo == lastPageNo
                                                                        ? 'opacity-50 user-select-none'
                                                                        : 'cursor-pointer fw-semibold'
                                                                }`}
                                                                onClick={() =>
                                                                    !loading &&
                                                                    issuerSearch_pageNo < lastPageNo &&
                                                                    setIssuerSearch_pageNo(issuerSearch_pageNo + 1)
                                                                }
                                                            >
                                                                Next <FontAwesomeIcon size="sm" icon={faAngleRight} color="#999" />
                                                            </span>
                                                        </span>
                                                        <span
                                                            className="border rounded p-1 mb-1 d-flex align-items-center"
                                                            style={{ width: '40%' }}
                                                        >
                                                            <label htmlFor="search_issuer">
                                                                <FontAwesomeIcon size="1x" icon={faSearch} color="#999" />
                                                            </label>
                                                            <input
                                                                type="text"
                                                                id="search_issuer"
                                                                placeholder="Search issuers"
                                                                className="border-0 ps-1 text-xs outline-none"
                                                                value={issuerSearchQuery}
                                                                onChange={handleIssuerInputChange}
                                                            />
                                                            {!!issuerSearchQuery && (
                                                                <FontAwesomeIcon
                                                                    size="lg"
                                                                    icon={faXmark}
                                                                    color="#999"
                                                                    className="cursor-pointer"
                                                                    onClick={() => handleIssuerInputChange({ target: { value: '' } })}
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                    {!isIssuerListByCharLoader ? (
                                                        <div className="d-flex flex-column gap-1 mt-1">
                                                            {Array(7)
                                                                .fill(0)
                                                                .map((_, index) => (
                                                                    <Skeleton key={index} height={27} />
                                                                ))}
                                                        </div>
                                                    ) : (
                                                        !!issuerListByChar?.issuer.length &&
                                                        issuerListByChar?.issuer?.map((_issuer, index) => (
                                                            <Link
                                                                href={'/issuer-bonds/' + setEncodetext(_issuer.cleaned_issuername)}
                                                                key={index}
                                                                className={`p-2 no-underline fw-semibold m-0 d-block text-start shibui-text text-md ${
                                                                    index % 2 === 0 && 'bg-light'
                                                                }`}
                                                                style={{ fontSize: 11 }}
                                                            >
                                                                {_issuer.cleaned_issuername}
                                                            </Link>
                                                        ))
                                                    )}
                                                    {isIssuerListByCharLoader && (
                                                        <Link
                                                            className="align-content-end text-sm mt-2 flex-grow-1 no-underline show-more-cus shibui-text text-md fw-semibold"
                                                            href={'/issuers/' + issuer_char}
                                                        >
                                                            Show more{' '}
                                                            <FontAwesomeIcon
                                                                size="lg"
                                                                icon={faCircleChevronRight}
                                                                className="pe-1"
                                                                color="#294672"
                                                            />{' '}
                                                        </Link>
                                                    )}
                                                </div>
                                            )
                                        ) : (
                                            <div className="d-grid place-items-center w-100 align-content-center">
                                                <span>
                                                    <FontAwesomeIcon icon={faGear} className="rotate-infinite me-2" />
                                                    <span className="fs-6">Loading...</span>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </span>
                        </div>
                    </div>
                    <Suspense>
                        {!isMobile && (
                            <SearchForm
                                filterObj={filterObj}
                                setFilterObj={setFilterObj}
                                recordsPerPage={recordsPerPage}
                                filterData={filterDataList}
                                handleGetList={handleGetList}
                                handleReset={handleReset}
                                is_loading={isBoandsLoader || loading}
                                testSlider={testSlider}
                                setTestSilder={setTestSilder}
                                setFilterDataList={setFilterDataList}
                                filterCloseList={filterCloseList}
                                noFilterBonds={noFilterBonds}
                                isLoggedIn={isLoggedIn}
                                {...rest}
                            />
                        )}
                    </Suspense>
                </div>
                <div className="col-sm-12 col-lg-10 border-start">
                    <SearchFilters
                        appliedFiltersContainerElement={appliedFiltersContainerElement}
                        filterData={filterDataList}
                        setFilterCloselist={setFilterCloselist}
                        countryData={countryData}
                        currencyData={currencyData}
                        min_maturity_year={min_maturity_year}
                        max_maturity_year={max_maturity_year}
                        paginationData={currentData}
                        isMobileView={false}
                        // setFiltersCount={setFiltersCount}
                        filterCloseList={filterCloseList}
                        setFilterObj={setFilterObj}
                    />
                    <div className="table-container-height-set">
                        <table className={`table table-responsive`}>
                            <thead className="table-secondary sticky-top">
                                <tr style={{ zIndex: 2 }}>
                                    <th
                                        className="text-sm"
                                        style={{ width: '35%', cursor: 'pointer' }}
                                        onClick={() => handleSort('bond_name')}
                                        title={
                                            sortColumn === 'bond_name' ? `Sorting BOND NAME ${sortDirection === 'asc' ? 'A-Z' : 'Z-A'}` : ''
                                        }
                                    >
                                        BOND NAME {getSortIndicator('bond_name')}
                                    </th>
                                    <th
                                        className="text-sm"
                                        style={{ width: '15%', cursor: 'pointer' }}
                                        onClick={() => handleSort('country_name')}
                                        title={
                                            sortColumn === 'country_name'
                                                ? `Sorting COUNTRY NAME ${sortDirection === 'asc' ? 'A-Z' : 'Z-A'}`
                                                : ''
                                        }
                                    >
                                        COUNTRY {getSortIndicator('country_name')}
                                    </th>
                                    <th
                                        className="text-sm"
                                        style={{ width: '11%', cursor: 'pointer' }}
                                        onClick={() => handleSort('maturity')}
                                        title={
                                            sortColumn === 'maturity' ? `Sorting Maturity ${sortDirection === 'asc' ? 'A-Z' : 'Z-A'}` : ''
                                        }
                                    >
                                        MATURITY {getSortIndicator('maturity')}
                                    </th>
                                    <th
                                        className="text-sm text-center"
                                        style={{ width: '11%', cursor: 'pointer' }}
                                        onClick={() => handleSort('coupon')}
                                        title={sortColumn === 'coupon' ? `Sorting Coupon ${sortDirection === 'asc' ? 'A-Z' : 'Z-A'}` : ''}
                                    >
                                        COUPON(%) {getSortIndicator('coupon')}
                                    </th>
                                    <th
                                        className="text-sm text-center"
                                        style={{ width: '11%', cursor: 'pointer' }}
                                        onClick={() => handleSort(yieldColumn)}
                                        title={
                                            sortColumn === yieldColumn
                                                ? `Sorting ${yieldColumn} ${sortDirection === 'asc' ? 'A-Z' : 'Z-A'}`
                                                : ''
                                        }
                                    >
                                        <select
                                            onChange={(e) => {
                                                setYieldColumn(e.target.value);
                                                handleYieldClick(e.target.value);
                                            }}
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                borderRadius: '.25rem',
                                            }}
                                            className="screener-table-header-select"
                                        >
                                            <option value="market_yield" defaultChecked>
                                                Yield(%)
                                            </option>
                                            <option value="ytm">YTM(%)</option>
                                            <option value="ytw">YTW(%)</option>
                                        </select>
                                        {getSortIndicator(yieldColumn)}
                                    </th>
                                    <th
                                        className="text-sm text-center"
                                        style={{ width: '11%', cursor: 'pointer' }}
                                        onClick={() => handleSort(changePercentageColumn)}
                                        title={
                                            sortColumn === changePercentageColumn
                                                ? `Sorting ${changePercentageColumn} ${sortDirection === 'asc' ? 'A-Z' : 'Z-A'}`
                                                : ''
                                        }
                                    >
                                        <select
                                            onChange={handleChangePercent}
                                            aria-label="Select Change % or Change"
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                borderRadius: '.25rem',
                                            }}
                                            className="screener-table-header-select"
                                        >
                                            <option value="change">Change</option>
                                            <option value="changePercent">Change %</option>
                                        </select>
                                        <span
                                            onClick={() => handleSort(showChange_percent ? 'changepercentage' : 'chng')}
                                            id={showChange_percent ? 'changepercentage' : 'chng'}
                                        ></span>{' '}
                                        {getSortIndicator(changePercentageColumn)}
                                    </th>

                                    {isLoggedIn ? (
                                        <th className="text-sm" style={{ width: '3%', cursor: 'pointer' }}>
                                            <span className="trans-position-head" style={{ color: 'transparent' }}></span>
                                        </th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {loading || (isBoandsLoader && hasSkippedFirstFetch.current) ? (
                                    <TableLazyLoading isLoggedIn={isLoggedIn} />
                                ) : !displayData.bonds || displayData.bonds.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center">
                                            No data found!
                                        </td>
                                    </tr>
                                ) : (
                                    displayData.bonds.map((bond: Bond) => (
                                        <tr id={bond.isin} key={bond.isin}>
                                            <td className="bg-light-subtle text-sm">
                                                <span
                                                    className={`${
                                                        bond.status === 'Active' ? 'link-primary ' : 'text-muted'
                                                    } cursor-pointer d-flex text-black align-items-end`}
                                                >
                                                    <Link
                                                        href={`/bonds/${bond.isin}?from=screener`}
                                                        onClick={() => handleBondNameClick(bond.isin)}
                                                        className="d-block text-sm  no-underline text-truncate  text-black"
                                                    >
                                                        <span className="font-semibold fw-medium">
                                                            {bond?.isin === null || bond?.isin === '' ? '—' : bond?.bond_name}
                                                        </span>
                                                        <span className="d-flex font-light align-items-center gap-1">{bond?.issuer}</span>
                                                    </Link>
                                                    {isLoggedIn &&
                                                        (bond?.follow_bond ? (
                                                            <BookmarkCheckFill
                                                                color="#294772"
                                                                className="cursor-pointer mb-1 ms-1"
                                                                title="Unfollow"
                                                                onClick={() => handleFollowBond(bond.isin)}
                                                            />
                                                        ) : (
                                                            <Bookmark
                                                                color="#294772"
                                                                className="cursor-pointer mb-1 ms-1"
                                                                title="Follow"
                                                                onClick={() => handleFollowBond(bond.isin)}
                                                            />
                                                        ))}
                                                </span>
                                            </td>

                                            <td className="bg-light-subtle text-sm">
                                                {bond?.country_name === null ? '—' : bond?.country_name}
                                            </td>
                                            <td className="text-nowrap bg-light-subtle text-sm">
                                                {bond?.maturity === null ? '—' : bond?.maturity}
                                            </td>
                                            <td className="text-end px-4 bg-light-subtle text-sm" width="10%">
                                                {bond?.coupon === null
                                                    ? bond?.description?.slice(0, 2) === 'ZC'
                                                        ? '0.00'
                                                        : '—'
                                                    : bond?.coupon?.toFixed(3)}
                                            </td>
                                            <td className="text-end px-4 bg-light-subtle text-sm">
                                                {yieldColumn === 'market_yield'
                                                    ? bond?.market_yield === null
                                                        ? bond?.description?.slice(0, 2) === 'ZC'
                                                            ? '0.00'
                                                            : '—'
                                                        : bond?.market_yield?.toFixed(2)
                                                    : ''}
                                                {yieldColumn === 'ytm' ? (bond?.ytm === null ? '—' : bond?.ytm?.toFixed(3)) : ''}
                                                {yieldColumn === 'ytw' ? (bond?.ytw === null ? '—' : bond?.ytw?.toFixed(3)) : ''}
                                            </td>
                                            <td className={`text-end px-4 bg-light-subtle text-sm`}>
                                                {showChange_percent ? (
                                                    <span
                                                        className={`${
                                                            bond.changepercentage === null || bond.changepercentage === 0
                                                                ? 'text-dark'
                                                                : bond.changepercentage > 0
                                                                ? 'text-success'
                                                                : 'text-danger'
                                                        }`}
                                                    >
                                                        {bond?.changepercentage === null ? '—' : bond.changepercentage?.toFixed(3)}
                                                    </span>
                                                ) : (
                                                    <span
                                                        className={`${
                                                            bond.chng === null || bond.chng === 0
                                                                ? 'text-dark'
                                                                : bond.chng > 0
                                                                ? 'text-success'
                                                                : 'text-danger'
                                                        }`}
                                                    >
                                                        {bond?.chng === null || bond?.chng === 0
                                                            ? '—'
                                                            : bond?.chng > 0
                                                            ? '+' + bond?.chng?.toFixed(3)
                                                            : bond?.chng?.toFixed(3)}
                                                    </span>
                                                )}
                                            </td>

                                            {isLoggedIn ? (
                                                <td className="text-center bg-light-subtle">
                                                    <DropdownButton
                                                        id="dropdown-basic-button"
                                                        className="btn-sm"
                                                        title={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                                    >
                                                        <Dropdown.Item
                                                            className="text-success"
                                                            onClick={() => handleShowPosition(bond.isin, 'buyPrice')}
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="text-success" /> Buy
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                            className="text-danger"
                                                            onClick={() => handleShowPosition(bond.isin, 'sellPrice')}
                                                        >
                                                            <FontAwesomeIcon icon={faMinus} className="text-danger" /> Sell
                                                        </Dropdown.Item>
                                                    </DropdownButton>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <Pagination
                            data={currentData}
                            setPageNumber={handlePageChange}
                            setPerpage={handleRecordsPerPage}
                            isLoading={isBoandsLoader || loading}
                        />
                    </div>
                    <BondPosition
                        show={showPosition}
                        selectedAction={selectedAction}
                        setShowPosition={setShowPosition}
                        bondId={bondId}
                        onclose={handleClosePosition}
                    />
                </div>
            </div>
        </div>
    );
};

// Redux connections remain the same
const mapStatesToProps = ({
    bonds: {
        isIssuerFirstletterListLoader = false,
        isIssuerFirstletterListData = [],
        bondsData = {},
        clientIp = '',
        noFilterBonds = null,
        isBoandsLoader = false,
        isSearchCreateLoader = false,
        isSearchListLoader = false,
        isSearchDetailListLoader = false,
        searchFilterDetailData = [],
        followBond = {},
        trigger,
        bondsFulltextHandle,
        isFollowBondLoader = false,
        isbondSearchFilterListLoader = false,
        bondSearchFilterList = [],
        isIssuerListByCharLoader = false,
        isIssuerListByCharData = {},
    },
    common: { countryData, currencyData, maturityData },
    authenticate: {},
}: RootState) => {
    return {
        isIssuerFirstletterListData,
        isIssuerFirstletterListLoader,
        bondSearchFilterList,
        bondsData,
        clientIp,
        noFilterBonds,
        isBoandsLoader,
        isSearchCreateLoader,
        isSearchListLoader,
        searchFilterDetailData,
        followBond,
        trigger,
        bondsFulltextHandle,
        isSearchDetailListLoader,
        isFollowBondLoader,
        isbondSearchFilterListLoader,
        countryData,
        currencyData,
        maturityData,
        isIssuerListByCharLoader,
        isIssuerListByCharData,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            ...bondsAction,
            ...commonAction,
            ...dashboardAction,
            ...authenticateAction,
        },
        dispatch
    );
};

const connector = connect(mapStatesToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(memo(BondSearch));
